import React, { useState, useEffect, useContext, useRef } from "react"
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom"
import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material"
import { useSnackbar } from "notistack"
import { Trans } from "react-i18next"
import i18n from "i18n"

import {
  adaptInvoiceForBackend,
  formatDate,
  validateInvoiceForm,
} from "./utils/invoiceUtils"
import AuthContext from "context/auth/authContext"
import Header from "./Invoice/Header"
import InvoiceInfo from "./Invoice/IvoiceInfo"
import InvoiceConcepts from "./Invoice/IvoiceConcepts"
import InvoicePayment from "./Invoice/IvoicePayment"
import InvoiceNotes from "./Invoice/InvoiceNotes"
import ContactModal from "components/Contacts/ContactModal"
import {
  useContact,
  useCompanies,
  useCompany,
  useContactTags,
  useEditContact,
} from "components/Contacts/hooks/useContacts"
import { useQueryClient } from "@tanstack/react-query"
import {
  useCreateInvoice,
  useSingleInvoice,
  useEditInvoice,
  useIssueInvoice,
  useUpdatePaymentStatus,
  useResendToInvopop,
} from "./hooks/useInvoices"
import { useSeries } from "components/ProfileRefinament/series/hooks/useSeries"
import NoActiveSerieAlert from "components/ProfileRefinament/series/NoActiveSerieAlert"
import { buildSerieNextNumber } from "components/ProfileRefinament/series/buildSeriePreview"

const translateReason = reason => {
  if (i18n.exists(`invoices.issueReasons.${reason}`)) {
    return i18n.t(`invoices.issueReasons.${reason}`)
  }
  if (reason.startsWith("Series '") && reason.includes("not found")) {
    return i18n.t("invoices.issueReasons.seriesNotFound")
  }
  if (reason.startsWith("Series '") && reason.includes("exhausted")) {
    return i18n.t("invoices.issueReasons.seriesExhausted")
  }
  if (
    reason.startsWith("Invoice ") &&
    reason.includes("missing 'invoiceSerie'")
  ) {
    return i18n.t("invoices.issueReasons.missingSerieForInvoice")
  }
  return reason
}

const translateGoblError = ({ code, message, path }) => {
  if (code && i18n.exists(`invoices.goblErrors.${code}`)) {
    return i18n.t(`invoices.goblErrors.${code}`)
  }
  return path ? `${path}: ${message}` : message
}

const translateTooLongField = path => {
  const parts = String(path || "").split(".")
  const field = parts[parts.length - 1]
  const key = `invoices.fieldTooLongFields.${field}`
  if (!i18n.exists(key)) return path
  const position = Number(parts[parts.length - 2])
  return i18n.t(key, {
    position: Number.isNaN(position) ? "" : position + 1,
  })
}

const getFieldTooLongMessage = error => {
  const data = error?.response?.data
  if (data?.code !== "FIELD_TOO_LONG") return null
  const errors = data.errors || []
  if (!errors.length) return null

  if (errors.length === 1) {
    const [first] = errors
    return i18n.t("invoices.fieldTooLong", {
      field: translateTooLongField(first.path),
      limit: first.limit,
      actual: first.actual,
    })
  }

  const fields = errors
    .map(e =>
      i18n.t("invoices.fieldTooLongItem", {
        field: translateTooLongField(e.path),
        limit: e.limit,
        actual: e.actual,
      }),
    )
    .join("; ")
  return i18n.t("invoices.fieldTooLongMultiple", { fields })
}

const getIssueErrorMessage = error => {
  const data = error?.response?.data
  const tooLong = getFieldTooLongMessage(error)
  if (tooLong) return tooLong
  if (!data?.reason) return i18n.t("invoices.issueError")

  if (
    data.code === "GOBL_VALIDATION" &&
    Array.isArray(data.errors) &&
    data.errors.length
  ) {
    const detail = data.errors
      .map(translateGoblError)
      .filter(Boolean)
      .join("; ")
    return i18n.t("invoices.issueErrorWithDetail", { detail })
  }

  return i18n.t("invoices.issueErrorWithDetail", {
    detail: translateReason(data.reason),
  })
}

// The contact enum predates the invoice one and spells the card option differently.
const CONTACT_TO_INVOICE_PAYMENT_METHOD = {
  creditCard: "card",
  paypal: "other",
}

const initialInvoiceState = {
  projectId: "",
  invoiceNumber: "",
  invoiceType: "standard",
  contact: "",
  company: "",
  oldInvoiceId: undefined,
  issueDate: new Date().toISOString().split("T")[0],
  issued: false,
  dueDate: "",
  paymentMethod: "transfer",
  paymentStatus: "unpaid",
  invoiceConcepts: [],
  notes: "",
  iban: "",
  subtotal: 0,
  tax: 0,
  total: 0,
  discount: 0,
}

const CORRECTION_TYPE_TO_INVOICE_TYPE = {
  replace: "corrective",
  credit: "credit-note",
  debit: "debit-note",
}

const normalizeCertificationPart = certificationPart => {
  if (!certificationPart || typeof certificationPart !== "object") return null

  const certId = certificationPart?.certId
  const partidaId = certificationPart?.partidaId
  if (!certId || !partidaId) return null

  return {
    projectId: certificationPart?.projectId ?? null,
    certId,
    partidaId,
    amount: Number(certificationPart?.amount) || 0,
  }
}

const normalizePrefilledConcepts = concepts => {
  if (!Array.isArray(concepts)) return []

  return concepts.map((concept, index) => ({
    ...concept,
    description: concept?.description || "",
    quantity: 1,
    price: Number(concept?.price) || 0,
    taxes: [],
    discount: 0,
    position: typeof concept?.position === "number" ? concept.position : index,
    certsAssociated: Array.isArray(concept?.certsAssociated)
      ? concept.certsAssociated.map(normalizeCertificationPart).filter(Boolean)
      : [],
  }))
}

const Invoice = () => {
  const { invoiceId, projectId } = useParams()
  const [searchParams] = useSearchParams()
  const correctsInvoiceIdFromQuery = searchParams.get("correctsInvoice") || null
  const correctionTypeFromQuery = searchParams.get("correctionType") || null
  const voidTotalFromQuery = searchParams.get("voidTotal") === "true"
  const location = useLocation()

  const navigate = useNavigate()
  const {
    user,
    organizationFields: orgFieldsContext,
    getOrganizationNew,
  } = useContext(AuthContext)
  const { enqueueSnackbar } = useSnackbar()
  const organizationId = user ? user["custom:organizationId"] : undefined

  const isNewRoute = invoiceId === "new"
  const prefilledConcepts = isNewRoute
    ? normalizePrefilledConcepts(location.state?.prefilledConcepts)
    : []
  const [isNew, setIsNew] = useState(isNewRoute)
  const [invoiceData, setInvoiceData] = useState({
    ...initialInvoiceState,
    projectId: isNewRoute ? projectId || "" : "",
    invoiceConcepts: prefilledConcepts,
  })
  const [hasValidationErrors, setHasValidationErrors] = useState(false)
  const [invalidContact, setInvalidContact] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isIssuing, setIsIssuing] = useState(false)
  const [confirmIssueOpen, setConfirmIssueOpen] = useState(false)
  const [organizationFields, setOrganizationFields] = useState(orgFieldsContext)
  const [selectedSerieSK, setSelectedSerieSK] = useState(null)
  const migratingRef = useRef(false)
  const correctiveClonedRef = useRef(false)
  const savedSnapshotRef = useRef(null)

  const [editContactModalOpen, setEditContactModalOpen] = useState(false)
  const [pendingEditContactId, setPendingEditContactId] = useState(null)
  // Which client the user hand-picked a method for. Scoped to that client so choosing
  // a different one -- or clearing and re-picking -- applies its preference again.
  const manualPaymentMethodForRef = useRef(null)

  // The client SK is `companyId#contactId`; contability lives on the company, and the
  // light listings that feed the selectors do not carry it.
  const selectedCompanyId =
    typeof invoiceData.contact === "string" && invoiceData.contact
      ? invoiceData.contact.split("#")[0]
      : ""
  const selectedCompanyQuery = useCompany(
    organizationId,
    selectedCompanyId,
    !!selectedCompanyId && !!organizationId && !invoiceData.issued,
  )
  const selectedCompanyContability =
    selectedCompanyQuery.data?.contability || null

  useEffect(() => {
    if (invoiceData.issued) return
    if (!selectedCompanyId) {
      // Clearing the client discards the manual pick, so re-selecting the same one
      // applies its preference again.
      manualPaymentMethodForRef.current = null
      return
    }
    if (manualPaymentMethodForRef.current === selectedCompanyId) return

    const method = selectedCompanyContability?.paymentMethod
    if (!method) return

    const mapped = CONTACT_TO_INVOICE_PAYMENT_METHOD[method] || method
    setInvoiceData(prev =>
      prev.paymentMethod === mapped ? prev : { ...prev, paymentMethod: mapped },
    )
  }, [selectedCompanyContability, selectedCompanyId, invoiceData.issued])

  const queryClient = useQueryClient()
  const editContactMutation = useEditContact()
  const fullContactQuery = useContact(
    organizationId,
    pendingEditContactId,
    !!pendingEditContactId && !!organizationId,
  )
  const { data: contactCompanies = [] } = useCompanies(
    organizationId,
    editContactModalOpen && !!organizationId,
  )
  const { data: contactTags = [] } = useContactTags(
    organizationId,
    editContactModalOpen && !!organizationId,
  )

  const [editContactData, setEditContactData] = useState(null)

  useEffect(() => {
    if (!pendingEditContactId) return
    if (fullContactQuery.data) {
      setEditContactData(fullContactQuery.data)
      setEditContactModalOpen(true)
      setPendingEditContactId(null)
    }
  }, [pendingEditContactId, fullContactQuery.data])

  const handleOpenEditContact = () => {
    const contactSK = invoiceData.contact
    if (contactSK) setPendingEditContactId(contactSK)
  }

  const handleSaveEditContact = (contactData, companyId) => {
    editContactMutation.mutate(
      { contactData, companyId },
      {
        onSuccess: updatedContact => {
          setEditContactModalOpen(false)
          queryClient.invalidateQueries({ queryKey: ["contacts"] })
          if (updatedContact?.SK) {
            queryClient.invalidateQueries({
              queryKey: ["contact", organizationId, updatedContact.SK],
            })
          }
          const adapted = adaptInvoiceForBackend(invoiceData, {
            invoiceSerie: selectedSerieSK,
          })
          editInvoice.mutate(
            { organizationId, invoice: adapted },
            {
              onSuccess: () => {
                savedSnapshotRef.current = JSON.stringify(adapted)
                enqueueSnackbar(
                  i18n.t("contacts.snackbar.successfullyUpdated"),
                  { variant: "success" },
                )
              },
              onError: () => {
                enqueueSnackbar(
                  i18n.t("contacts.snackbar.unSuccessfullyUpdated"),
                  { variant: "error" },
                )
              },
            },
          )
        },
        onError: () => {
          enqueueSnackbar(i18n.t("contacts.snackbar.unSuccessfullyUpdated"), {
            variant: "error",
          })
        },
      },
    )
  }

  const createInvoice = useCreateInvoice()
  const editInvoice = useEditInvoice()
  const updatePaymentStatus = useUpdatePaymentStatus()
  const issueInvoice = useIssueInvoice()
  const resendToInvopop = useResendToInvopop()
  const getInvoice = useSingleInvoice(
    organizationId,
    invoiceId,
    !isNew && !!organizationId && !!invoiceId && invoiceId !== "new",
  )

  const correctsInvoiceIdFromBody =
    getInvoice.data?.correctsInvoice || invoiceData.correctsInvoice || null
  const correctsInvoiceId =
    correctsInvoiceIdFromQuery || correctsInvoiceIdFromBody || null

  const getCorrectsInvoice = useSingleInvoice(
    organizationId,
    correctsInvoiceId,
    !!organizationId && !!correctsInvoiceId,
  )

  const correctedById = invoiceData.correctedBy || null
  const getCorrectedByInvoice = useSingleInvoice(
    organizationId,
    correctedById,
    !!organizationId && !!correctedById,
  )

  const corrections = invoiceData.corrections || []

  const isAnyCorrective =
    invoiceData.invoiceType === "corrective" ||
    invoiceData.invoiceType === "credit-note" ||
    invoiceData.invoiceType === "debit-note"
  const serieType = isAnyCorrective ? "correctives" : "invoices"

  const invoiceSeries = useSeries(organizationId, serieType, {
    enabled: !invoiceData.issued && !!organizationId,
  })
  const activeInvoiceSeries = (invoiceSeries.data || []).filter(
    s => !s.archived && !s.exhausted,
  )
  const showNoActiveSerieBlock =
    !invoiceData.issued &&
    !invoiceSeries.isLoading &&
    activeInvoiceSeries.length === 0
  const selectedSerie =
    activeInvoiceSeries.find(s => s.SK === selectedSerieSK) || null
  const pendingInvoiceNumber = selectedSerie
    ? buildSerieNextNumber(selectedSerie)
    : ""

  useEffect(() => {
    console.log("Fetching organization fields for invoice...")
    let isMounted = true
    const fetchOrgFields = async () => {
      if (!organizationId) return

      const res = await getOrganizationNew(organizationId)
      if (!res || !isMounted) return
      setOrganizationFields(res)
    }
    fetchOrgFields()
    return () => {
      isMounted = false
    }
  }, [organizationId, invoiceId])

  useEffect(() => {
    setOrganizationFields(orgFieldsContext)
  }, [orgFieldsContext])

  useEffect(() => {
    const orgIban = organizationFields?.iban
    if (!orgIban || !isNew || invoiceData.issued) return

    // Seeds the collection account on a new invoice only. It stays editable, because
    // organizations that bill from several of their own accounts pick per invoice.
    setInvoiceData(prev => (prev.iban ? prev : { ...prev, iban: orgIban }))
  }, [organizationFields, isNew, invoiceData.issued])

  useEffect(() => {
    if (!isNew && getInvoice.data) {
      setInvoiceData(prev => {
        let contactValue = getInvoice.data.contact
        if (
          contactValue &&
          typeof contactValue === "object" &&
          contactValue.SK
        ) {
          contactValue = contactValue.SK
        }
        return {
          ...prev,
          ...getInvoice.data,
          contact: contactValue,
          issueDate: formatDate(getInvoice.data.issueDate),
          dueDate: formatDate(getInvoice.data.dueDate),
        }
      })
      savedSnapshotRef.current = null
      if (getInvoice.data.invoiceSerie) {
        setSelectedSerieSK(getInvoice.data.invoiceSerie)
      }
    }
  }, [isNew, getInvoice.data])

  // Clone source invoice when creating a corrective via /invoice/new?correctsInvoice=...
  useEffect(() => {
    if (
      !isNew ||
      !correctsInvoiceIdFromQuery ||
      !getCorrectsInvoice.data ||
      correctiveClonedRef.current
    ) {
      return
    }
    const source = getCorrectsInvoice.data

    let contactValue = source.contact
    if (contactValue && typeof contactValue === "object" && contactValue.SK) {
      contactValue = contactValue.SK
    }

    const shouldCloneConcepts =
      !correctionTypeFromQuery ||
      correctionTypeFromQuery === "replace" ||
      voidTotalFromQuery

    const clonedConcepts = shouldCloneConcepts
      ? (source.invoiceConcepts || []).map(c => {
          const {
            SK: _sk,
            PK: _pk,
            position,
            description,
            quantity,
            price,
            discount,
            taxes,
            retentions,
            partidaId,
          } = c
          return {
            position,
            description,
            quantity,
            price: price || 0,
            discount: discount || 0,
            taxes: Array.isArray(taxes) ? taxes : [],
            retentions: Array.isArray(retentions) ? retentions : [],
            ...(partidaId ? { partidaId } : {}),
          }
        })
      : []

    const today = new Date().toISOString().split("T")[0]
    const resolvedInvoiceType =
      CORRECTION_TYPE_TO_INVOICE_TYPE[correctionTypeFromQuery] || "corrective"

    setInvoiceData(prev => ({
      ...prev,
      projectId: source.projectId || prev.projectId || "",
      contact: contactValue || "",
      company: typeof source.company === "string" ? source.company : "",
      invoiceConcepts: clonedConcepts,
      notes: source.notes || "",
      iban: source.iban || "",
      paymentMethod: source.paymentMethod || "transfer",
      paymentStatus: "unpaid",
      discount: shouldCloneConcepts ? source.discount || 0 : 0,
      issueDate: today,
      dueDate: "",
      invoiceNumber: "",
      issued: false,
      invoiceType: resolvedInvoiceType,
      correctsInvoice: correctsInvoiceIdFromQuery,
      reason: "",
      correctionReasonCode: "R1",
    }))

    correctiveClonedRef.current = true
  }, [
    isNew,
    correctsInvoiceIdFromQuery,
    correctionTypeFromQuery,
    voidTotalFromQuery,
    getCorrectsInvoice.data,
  ])

  const handleBack = () => {
    const returnTo = location.state?.returnTo
    if (returnTo) {
      const state = { fromInvoiceDraft: true }
      if (location.state?.invoiceCreated) state.invoiceCreated = true
      navigate(returnTo, { state })
      return
    }

    if (projectId) {
      navigate(`/invoices/${projectId}`)
    } else {
      navigate("/invoices/")
    }
  }

  const saveOrIssueInvoice = (options = { issue: false }) => {
    /* ===============================
           1. INVOICE ALREADY ISSUED: UPDATE PAYMENT STATUS ONLY
        =============================== */
    if (invoiceData.issued && !options.issue) {
      setIsSaving(true)
      updatePaymentStatus.mutate(
        {
          organizationId,
          invoice: {
            invoiceId: invoiceData.invoiceId,
            paymentStatus: invoiceData.paymentStatus,
          },
        },
        {
          onSuccess: () => {
            setIsSaving(false)
            enqueueSnackbar(i18n.t("invoices.editedSuccess"), {
              variant: "success",
            })
          },
          onError: () => {
            setIsSaving(false)
            enqueueSnackbar(i18n.t("invoices.editError"), { variant: "error" })
          },
        },
      )
      return
    }

    // Una factura emitida ya consumió su número: `issue` la rechaza con 409 y renumerarla
    // sería incorrecto. Lo que falta es completar el registro en Verifactu, así que se
    // reenvía el documento existente.
    if (options.issue && invoiceData.issued) {
      setIsIssuing(true)
      resendToInvopop.mutate(
        { organizationId, invoiceId: invoiceData.invoiceId },
        {
          onSuccess: async () => {
            setIsIssuing(false)
            enqueueSnackbar(i18n.t("invoices.resendSuccess"), {
              variant: "success",
            })
            await getInvoice.refetch()
          },
          onError: () => {
            setIsIssuing(false)
            enqueueSnackbar(i18n.t("invoices.resendError"), {
              variant: "error",
            })
          },
        },
      )
      return
    }

    /* ===============================
           2. VALIDATIONS
        =============================== */
    const validationError = validateInvoiceForm(
      invoiceData,
      invalidContact,
      activeInvoiceSeries,
      selectedSerieSK,
      { isIssuing: options.issue, contability: selectedCompanyContability },
    )

    if (validationError) {
      enqueueSnackbar(i18n.t(validationError), { variant: "error" })
      setHasValidationErrors(true)
      return
    }

    setHasValidationErrors(false)

    if (!organizationId) {
      console.error("No organizationId found, cannot crear/editar invoice")
      return
    }

    options.issue ? setIsIssuing(true) : setIsSaving(true)

    /* ===============================
           3. PAYLOAD PREPARATION
        =============================== */
    const adapted = buildAdapted()

    const hasOldInvoice = Boolean(invoiceData.oldInvoiceId)
    migratingRef.current = !isNew && hasOldInvoice

    if (migratingRef.current && !options.issue) {
      adapted.oldInvoiceId = null
    }

    /* ===============================
           4. CALLBACKS
        =============================== */
    const onSuccessCreate = data => {
      if (data?.error || data?.message) {
        setIsSaving(false)
        setIsIssuing(false)
        enqueueSnackbar(i18n.t("invoices.createError"), { variant: "error" })
        return
      }

      const createdInvoiceUrl = `/invoice/${data.invoiceId}`

      if (options.issue) {
        issueInvoice.mutate(
          { organizationId, invoice: { invoiceId: data.invoiceId } },
          {
            onSuccess: async () => {
              setIsIssuing(false)
              enqueueSnackbar(i18n.t("invoices.issuedSuccess"), {
                variant: "success",
              })
              navigate(createdInvoiceUrl, {
                state: {
                  returnTo: location.state?.returnTo,
                  invoiceCreated: true,
                },
              })
              setIsNew(false)
              await getInvoice.refetch()
            },
            onError: error => {
              setIsIssuing(false)
              enqueueSnackbar(getIssueErrorMessage(error), {
                variant: "error",
              })
              setTimeout(() => {
                navigate(createdInvoiceUrl, {
                  state: { returnTo: location.state?.returnTo },
                  replace: true,
                })
                setIsNew(false)
              }, 2000)
            },
          },
        )
        return
      }

      setIsSaving(false)
      enqueueSnackbar(i18n.t("invoices.createdSuccess"), { variant: "success" })
      navigate(createdInvoiceUrl, {
        state: {
          returnTo: location.state?.returnTo,
          invoiceCreated: true,
        },
      })
      setIsNew(false)
    }

    const onSuccessEdit = () => {
      if (migratingRef.current && !options.issue) {
        setInvoiceData(prev => ({ ...prev, oldInvoiceId: null }))
      }

      savedSnapshotRef.current = JSON.stringify(adapted)
      if (!options.issue) {
        enqueueSnackbar(i18n.t("invoices.editedSuccess"), {
          variant: "success",
        })
      }

      if (options.issue) {
        issueInvoice.mutate(
          {
            organizationId,
            invoice: adapted,
            isOldInvoice: migratingRef.current,
          },
          {
            onSuccess: async () => {
              setIsIssuing(false)
              enqueueSnackbar(i18n.t("invoices.issuedSuccess"), {
                variant: "success",
              })
              await getInvoice.refetch()
            },
            onError: error => {
              setIsIssuing(false)
              enqueueSnackbar(getIssueErrorMessage(error), {
                variant: "error",
              })
            },
          },
        )
        return
      }

      setIsSaving(false)
    }

    const onErrorCommon = error => {
      setIsSaving(false)
      setIsIssuing(false)
      const tooLong = getFieldTooLongMessage(error)
      enqueueSnackbar(
        tooLong ||
          (options.issue
            ? i18n.t("invoices.issueError")
            : i18n.t("invoices.editError")),
        { variant: "error" },
      )
    }

    /* ===============================
           5. MUTATION
        =============================== */
    const mutation = isNew ? createInvoice : editInvoice

    mutation.mutate(
      { organizationId, invoice: adapted },
      {
        onSuccess: isNew ? onSuccessCreate : onSuccessEdit,
        onError: onErrorCommon,
      },
    )
  }

  const buildAdapted = () => {
    const adapted = {
      ...adaptInvoiceForBackend(invoiceData, { invoiceSerie: selectedSerieSK }),
    }
    if (!invoiceData.issued) {
      delete adapted.invoiceNumber
    }
    return adapted
  }

  const handleSave = () => {
    saveOrIssueInvoice({ issue: false })
  }

  const handleIssueInvoice = () => {
    setConfirmIssueOpen(true)
  }

  const handleConfirmIssue = () => {
    setConfirmIssueOpen(false)

    const clientSK = invoiceData.contact || ""
    const [uuid1, uuid2] = clientSK.split("#")
    if (uuid1 && uuid2 && uuid1 === uuid2) {
      enqueueSnackbar(i18n.t("invoices.contactNeedsCompany"), {
        variant: "warning",
      })
      return
    }

    const adapted = buildAdapted()
    const currentSnapshot = JSON.stringify(adapted)
    const isDirty =
      !savedSnapshotRef.current || savedSnapshotRef.current !== currentSnapshot

    if (!isNew && !isDirty && !invoiceData.issued) {
      setIsIssuing(true)
      issueInvoice.mutate(
        {
          organizationId,
          invoice: adapted,
          isOldInvoice: migratingRef.current,
        },
        {
          onSuccess: async () => {
            setIsIssuing(false)
            enqueueSnackbar(i18n.t("invoices.issuedSuccess"), {
              variant: "success",
            })
            await getInvoice.refetch()
          },
          onError: error => {
            setIsIssuing(false)
            enqueueSnackbar(getIssueErrorMessage(error), { variant: "error" })
          },
        },
      )
      return
    }

    saveOrIssueInvoice({ issue: true })
  }

  const handleCancelIssue = () => {
    setConfirmIssueOpen(false)
  }

  const headerTitle = isNew
    ? i18n.t("invoices.newInvoice")
    : i18n.t("invoices.editInvoice")

  const isReady =
    (isNew &&
      (!correctsInvoiceIdFromQuery ||
        (!getCorrectsInvoice.isLoading && correctiveClonedRef.current))) ||
    (!isNew &&
      !getInvoice.isLoading &&
      !!getInvoice.data &&
      (!correctsInvoiceId || !getCorrectsInvoice.isLoading))
  const conceptsReadOnly =
    prefilledConcepts.length > 0 ||
    (invoiceData.invoiceConcepts || []).some(
      c => Array.isArray(c.certsAssociated) && c.certsAssociated.length > 0,
    )

  return (
    <>
      {isReady ? (
        <Box>
          {showNoActiveSerieBlock && <NoActiveSerieAlert />}
          {(() => {
            const invopopStatus = invoiceData.invopopData?.invopopStatus
            if (!["error", "failing", "rejected"].includes(invopopStatus))
              return null
            let faults = []
            try {
              const raw = invoiceData.invopopData?.faults
              faults = raw ? JSON.parse(raw) : []
            } catch (_) {
              faults = []
            }
            if (!faults.length) return null
            return (
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: "#fff3f3",
                  border: "1px solid #f5c6cb",
                  borderRadius: 1,
                }}
              >
                {faults.map((f, i) => (
                  <Box key={i} sx={{ fontSize: 13, color: "#721c24" }}>
                    {f.message || f.code}
                  </Box>
                ))}
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1, color: "#721c24", borderColor: "#f5c6cb" }}
                  disabled={
                    !!pendingEditContactId &&
                    (fullContactQuery.isLoading || fullContactQuery.isFetching)
                  }
                  onClick={handleOpenEditContact}
                >
                  {i18n.t("contacts.edit")}
                </Button>
              </Box>
            )
          })()}
          <Header
            showBack={true}
            onBack={handleBack}
            onSave={handleSave}
            onIssueInvoice={handleIssueInvoice}
            title={headerTitle}
            issued={invoiceData.issued}
            invoiceData={invoiceData}
            correctsInvoiceData={getCorrectsInvoice.data}
            correctedByInvoiceData={getCorrectedByInvoice.data}
            corrections={corrections}
            isSaving={isSaving}
            isIssuing={isIssuing}
            useInvopop={!!organizationFields?.useInvopop}
          />
          <InvoiceInfo
            invoiceData={invoiceData}
            setInvoiceData={setInvoiceData}
            hasValidationErrors={hasValidationErrors}
            onInvalidContact={setInvalidContact}
            organizationId={organizationId}
            isNew={isNew}
            selectedSerieSK={selectedSerieSK}
            setSelectedSerieSK={setSelectedSerieSK}
            serieType={serieType}
            contactData={
              getInvoice.data?.contact &&
              typeof getInvoice.data.contact === "object"
                ? getInvoice.data.contact
                : null
            }
          />
          <InvoiceConcepts
            invoiceData={invoiceData}
            setInvoiceData={setInvoiceData}
            hasValidationErrors={hasValidationErrors}
            conceptsReadOnly={conceptsReadOnly}
            corrections={corrections}
          />
          <InvoiceNotes
            invoiceData={invoiceData}
            setInvoiceData={setInvoiceData}
            issued={invoiceData.issued}
          />
          <InvoicePayment
            invoiceData={invoiceData}
            setInvoiceData={setInvoiceData}
            issued={invoiceData.issued}
            hasValidationErrors={hasValidationErrors}
            contability={selectedCompanyContability}
            onPaymentMethodEdited={() => {
              manualPaymentMethodForRef.current = selectedCompanyId
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Dialog open={confirmIssueOpen} onClose={handleCancelIssue}>
        <DialogTitle>{i18n.t("invoices.issueInvoice")}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Trans
              i18nKey="invoices.issueInvoiceConfirmation"
              values={{
                invoiceNumber:
                  invoiceData.invoiceNumber || pendingInvoiceNumber || "",
              }}
              components={[null, <strong key="invoice-number-preview" />]}
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelIssue} color="secondary">
            {i18n.t("invoices.cancel")}
          </Button>
          <Button
            onClick={handleConfirmIssue}
            color="branded"
            variant="contained"
          >
            {i18n.t("invoices.issue")}
          </Button>
        </DialogActions>
      </Dialog>

      <ContactModal
        open={editContactModalOpen}
        onClose={() => {
          setEditContactModalOpen(false)
          setEditContactData(null)
        }}
        onSave={handleSaveEditContact}
        contact={editContactData}
        companies={contactCompanies}
        contactTags={contactTags}
        organizationId={organizationId}
        queryClient={queryClient}
      />
    </>
  )
}

export default Invoice
